import { deleteSession, spotifyGetCurrentToken, spotifyRefreshToken } from "@api/index";
import { IToken } from "@ctypes/token";
import { ITokenExpiryPair } from "@shared/dto";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { AuthClientUtil } from "src/util/AuthClientUtil";
import { createSingleFlight } from "./singleFlight";

interface UserContextProps {
  token: IToken | null;
  isLoading: boolean;
  setToken: (token: IToken | null) => void;
  clearToken: () => Promise<void>;
  onNewToken: (accessToken: string, expiresAt: number) => IToken;
  refreshToken: () => Promise<IToken | null>;
}

const defaultUserContext: UserContextProps = {
  token: null,
  isLoading: true,
  setToken: () => {},
  clearToken: async () => {},
  onNewToken: (accessToken: string, expiresAt: number) => ({
    expiry: new Date(expiresAt),
    value: accessToken
  }),
  refreshToken: async () => null
};

export const UserContext = createContext<UserContextProps>(defaultUserContext);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<IToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const onNewToken = useCallback((accessToken: string, expiresAt: number) => {
    console.log("New token received");
    const nextToken = { expiry: new Date(expiresAt), value: accessToken } as IToken;
    setToken(nextToken);
    return nextToken;
  }, []);

  const refreshToken = useMemo(
    () =>
      createSingleFlight(async () => {
        const pair = await spotifyRefreshToken();
        return pair ? onNewToken(pair.access_token, pair.expires_at) : null;
      }),
    [onNewToken]
  );

  const clearToken = useCallback(async () => {
    await deleteSession();
    setToken(null);
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      // Fetch token from backend session (session cookie)
      // Backend will automatically refresh if expired
      try {
        const newToken = await spotifyGetCurrentToken(null);
        if (newToken !== null) {
          const validToken = newToken as ITokenExpiryPair;
          if (AuthClientUtil.isITokenExpiryPair(validToken)) {
            onNewToken(validToken.access_token, validToken.expires_at);
            setIsLoading(false);
            return; // Success!
          }
        }
      } catch (error) {
        console.warn("Failed to fetch token from session:", error);
      }

      console.log("No valid session found - user will need to re-authenticate");
      setIsLoading(false);
    };

    void fetchToken();
  }, [onNewToken]);

  const contextValue = useMemo(
    () => ({ token, isLoading, setToken, clearToken, onNewToken, refreshToken }),
    [token, isLoading, clearToken, onNewToken, refreshToken]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useTokenContext = () => {
  return useContext(UserContext);
};
